import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useOrderStore, useResourceStore } from './store';
import { loadReferenceData } from './services/dataService';
import Dashboard from './components/Dashboard/Dashboard';
import ReadmePage from './pages/ReadmePage';
import { ToastContainer, ToastStyles } from './components/UI/Toast';
import { ErrorBoundary } from './components/UI/ErrorBoundary';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingItems, setLoadingItems] = useState<string[]>([]);

  const setOrders = useOrderStore((state) => state.setOrders);
  const setMainWorkOrders = useOrderStore((state) => state.setMainWorkOrders);
  const setSubWorkOrders = useOrderStore((state) => state.setSubWorkOrders);
  const setPersons = useResourceStore((state) => state.setPersons);
  const setPersonHistory = useResourceStore((state) => state.setPersonHistory);
  const setMachines = useResourceStore((state) => state.setMachines);
  const setMaterials = useResourceStore((state) => state.setMaterials);
  const setProducts = useResourceStore((state) => state.setProducts);
  const setWorkflows = useResourceStore((state) => state.setWorkflows);

  useEffect(() => {
    const totalSteps = 9;
    let currentStep = 0;

    const updateProgress = (item: string) => {
      setLoadingItems(prev => [...prev, item]);
      currentStep++;
      setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
    };

    loadReferenceData()
      .then((data) => {
        updateProgress('訂單資料');
        setOrders(data.orders);

        updateProgress('主工單資料');
        setMainWorkOrders(data.mainWorkOrders);

        updateProgress('子工單資料');
        setSubWorkOrders(data.subWorkOrders);

        updateProgress('人員資源');
        setPersons(data.persons);

        updateProgress('人員歷史');
        setPersonHistory(data.personHistory);

        updateProgress('機台設備');
        setMachines(data.machines);

        updateProgress('物料資源');
        setMaterials(data.materials);

        updateProgress('產品資料');
        setProducts(data.products);

        updateProgress('工藝流程');
        setWorkflows(data.workflows);

        updateProgress('初始化完成');

        // 最後延遲一下讓使用者看到 100%
        setTimeout(() => setIsLoading(false), 300);
      })
      .catch((err) => {
        console.error('Failed to load reference data:', err);
        setError('無法載入資料，請重新整理頁面');
        setIsLoading(false);
      });
  }, [
    setOrders,
    setMainWorkOrders,
    setSubWorkOrders,
    setPersons,
    setPersonHistory,
    setMachines,
    setMaterials,
    setProducts,
    setWorkflows,
  ]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white grid-pattern animate-fade-in">
        <div className="text-center space-y-6 max-w-md">
          {/* 載入動畫 - 雙環效果 */}
          <div className="relative inline-block">
            <div className="w-20 h-20 border-4 border-[#0066cc]/20 rounded-full"></div>
            <div className="absolute top-0 left-0 w-20 h-20 border-4 border-[#0066cc] border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute top-2 left-2 w-16 h-16 border-4 border-transparent border-t-[#0066cc]/30 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>

            {/* 進度百分比 */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-[#0066cc]">{loadingProgress}%</span>
            </div>
          </div>

          {/* 載入訊息 */}
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-[#0f172a]">載入系統資料中</h2>
            <p className="text-[#64748b] text-sm">正在初始化製造排程系統...</p>
          </div>

          {/* 進度條 */}
          <div className="w-full max-w-xs mx-auto">
            <div className="h-2 bg-[#e2e8f0] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#0066cc] to-[#0088ff] transition-all duration-500 ease-out"
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
          </div>

          {/* 載入項目 */}
          <div className="space-y-2 text-left max-w-xs mx-auto">
            {loadingItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-[#64748b] animate-fade-in" style={{ animationDelay: `${index * 50}ms` }}>
                <span className="text-green-500">✓</span>
                <span>載入{item}</span>
              </div>
            ))}
            {loadingItems.length < 9 && (
              <div className="flex items-center gap-2 text-sm text-[#0066cc] animate-pulse">
                <span className="inline-block w-2 h-2 bg-[#0066cc] rounded-full animate-ping"></span>
                <span>載入中...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white grid-pattern animate-fade-in">
        <div className="card-elevated text-center max-w-md">
          {/* 錯誤圖示 */}
          <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-rose-100 border-2 border-red-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">⚠️</span>
          </div>

          {/* 錯誤訊息 */}
          <h2 className="text-2xl font-bold text-[#0f172a] mb-3">載入失敗</h2>
          <p className="text-[#64748b] mb-6">{error}</p>

          {/* 解決建議 */}
          <div className="bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-[#64748b] mb-2">可能的解決方式：</p>
            <ul className="text-sm text-[#475569] space-y-1 list-disc list-inside">
              <li>檢查網路連線是否正常</li>
              <li>確認瀏覽器支援本地儲存</li>
              <li>嘗試重新整理頁面</li>
            </ul>
          </div>

          {/* 重新整理按鈕 */}
          <button
            onClick={() => window.location.reload()}
            className="btn-primary w-full hover-lift"
          >
            🔄 重新整理頁面
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('全局錯誤捕獲:', error, errorInfo);
      }}
    >
      {/* 無障礙：跳過到主要內容 */}
      <a href="#main-content" className="skip-to-content">
        跳過到主要內容
      </a>

      <div id="main-content">
        <Routes>
          <Route path="/readme" element={<ReadmePage />} />
          <Route path="/" element={<Dashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      <ToastContainer />
      <ToastStyles />
    </ErrorBoundary>
  );
}

export default App;
